/**
 * Renders artifacts attached to the background star network.
 *
 * Wave 1 (MVP):
 *   - hundred_taps_star — one brighter twinkling star
 *   - thousand_taps_constellation — 3 stars connected in a triangle
 *
 * Wave 2 (2026-05-25) — deeper progression artifacts:
 *   - taps_10k_star_field — 5 ярких звёзд по разным позициям («поле»)
 *   - taps_100k_constellation_full — 7 точек + соединительные линии
 *   - days_7_root — мягкое коричневое свечение внизу экрана (корень)
 *   - days_30_tree — вертикальный subtle ствол с ветками слева
 *
 * Positions are stable (seeded by artifact id) so the same artifact lands
 * in the same place every render and across reloads — feels intentional,
 * not random clutter.
 */
import { useArtifactsStore } from './store.js';
import styles from './artifacts.module.css';

/** Bright star at a fixed location — left 18%, top 22%. */
function BrightStar(): JSX.Element {
  return (
    <div
      className={`${styles.brightStar ?? ''} ${styles.brightStarTwinkle ?? ''} ${styles.artifactReveal ?? ''}`}
      style={{ left: '18%', top: '22%' }}
      aria-hidden
    />
  );
}

/** Three stars in a triangle near the right edge of the screen. */
function Constellation(): JSX.Element {
  // Triangle apex positions (vw / vh fractions)
  const points = [
    { left: '78%', top: '20%' },
    { left: '84%', top: '32%' },
    { left: '74%', top: '35%' },
  ];
  return (
    <>
      {points.map((p, i) => (
        <div
          key={i}
          className={[
            styles.brightStar ?? '',
            styles.brightStarTwinkle ?? '',
            styles.artifactReveal ?? '',
          ].join(' ')}
          style={{ left: p.left, top: p.top, width: '6px', height: '6px' }}
          aria-hidden
        />
      ))}
      {/* SVG overlay with viewBox = full vw/vh percentages. Polyline draws
          lines between the same star positions using viewBox units (0-100). */}
      <svg
        className={styles.artifactReveal}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh',
          pointerEvents: 'none',
        }}
        aria-hidden
      >
        <polyline
          className={styles.constellationLine}
          points="78,20 84,32 74,35 78,20"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </>
  );
}

// =========================================================================
// Wave 2 (2026-05-25)
// =========================================================================

/**
 * 5 ярких звёзд по разным позициям — игрок натикал 10k раз. Stable
 * positions (hardcoded) — те же места при reload, не visual clutter.
 */
function StarField(): JSX.Element {
  const stars = [
    { left: '8%', top: '15%' },
    { left: '32%', top: '8%' },
    { left: '55%', top: '12%' },
    { left: '12%', top: '50%' },
    { left: '88%', top: '52%' },
  ];
  return (
    <>
      {stars.map((p, i) => (
        <div
          key={i}
          className={[
            styles.brightStar ?? '',
            styles.brightStarTwinkle ?? '',
            styles.artifactReveal ?? '',
          ].join(' ')}
          style={{ left: p.left, top: p.top, width: '5px', height: '5px' }}
          aria-hidden
        />
      ))}
    </>
  );
}

/**
 * Полное созвездие — 100k таптов. 7 точек + соединительные линии
 * в большую фигуру. Покрывает левую половину экрана.
 */
function FullConstellation(): JSX.Element {
  const stars = [
    { left: '6%', top: '60%' },
    { left: '18%', top: '70%' },
    { left: '28%', top: '62%' },
    { left: '38%', top: '78%' },
    { left: '20%', top: '85%' },
    { left: '8%', top: '80%' },
    { left: '32%', top: '90%' },
  ];
  return (
    <>
      {stars.map((p, i) => (
        <div
          key={i}
          className={[
            styles.brightStar ?? '',
            styles.brightStarTwinkle ?? '',
            styles.artifactReveal ?? '',
          ].join(' ')}
          style={{ left: p.left, top: p.top, width: '7px', height: '7px' }}
          aria-hidden
        />
      ))}
      <svg
        className={styles.artifactReveal}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh',
          pointerEvents: 'none',
        }}
        aria-hidden
      >
        {/* Соединяем все 7 в single polyline (path: 1→2→3→4→5→6→7→1) */}
        <polyline
          className={styles.constellationLine}
          points="6,60 18,70 28,62 38,78 20,85 8,80 32,90 6,60"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </>
  );
}

/**
 * 7 дней игры — корень внизу экрана. Мягкое коричнево-зелёное свечение
 * через всю нижнюю кромку. Сидит за ConsentBanner / overlay'ями (z=-1).
 */
function DailyRoot(): JSX.Element {
  return <div className={`${styles.dailyRoot ?? ''} ${styles.artifactReveal ?? ''}`} aria-hidden />;
}

/**
 * 30 дней — древо ствола. Вертикальная SVG silhouette на левом краю
 * экрана, очень subtle (opacity 0.15). С 3 «ветками».
 */
function DailyTree(): JSX.Element {
  return (
    <svg
      className={`${styles.dailyTree ?? ''} ${styles.artifactReveal ?? ''}`}
      viewBox="0 0 60 200"
      preserveAspectRatio="none"
      aria-hidden
    >
      {/* Ствол */}
      <path d="M30 200 L30 60" className={styles.dailyTreeTrunk} />
      {/* 3 ветки на разной высоте */}
      <path d="M30 150 Q22 145 15 130" className={styles.dailyTreeTrunk} />
      <path d="M30 110 Q40 105 48 90" className={styles.dailyTreeTrunk} />
      <path d="M30 80 Q18 70 12 50" className={styles.dailyTreeTrunk} />
    </svg>
  );
}

export function NetworkArtifacts(): JSX.Element {
  const unlocked = useArtifactsStore((s) => s.unlocked);
  const brightStar = unlocked.has('hundred_taps_star');
  const constellation = unlocked.has('thousand_taps_constellation');
  const starField = unlocked.has('taps_10k_star_field');
  const fullConst = unlocked.has('taps_100k_constellation_full');
  const dailyRoot = unlocked.has('days_7_root');
  const dailyTree = unlocked.has('days_30_tree');

  if (!brightStar && !constellation && !starField && !fullConst && !dailyRoot && !dailyTree) {
    return <></>;
  }

  return (
    <div className={styles.networkLayer} aria-hidden>
      {brightStar && <BrightStar />}
      {constellation && <Constellation />}
      {starField && <StarField />}
      {fullConst && <FullConstellation />}
      {dailyRoot && <DailyRoot />}
      {dailyTree && <DailyTree />}
    </div>
  );
}
